import React, { useCallback, useMemo, useRef } from 'react';
import type { AnalysisProps } from '../../types';
import type { Analysis } from 'venn-api';
import { useRecoilValue } from 'recoil';
import {
  blockDisplayHeader,
  blockFonts,
  blockLimitedRequestSubjects,
  flattenedProxyMapState,
  getCustomFontSize,
  studioPrintOrientationType,
  subjectAsOfDate,
} from 'venn-state';
import { compact, isEqual } from 'lodash';
import { MAX_INVESTMENTS } from '../../../charts/analysis-charts/correlation-analysis/logic';
import { portfolioCorrelationExtractor } from '../../../studio-blocks/components/charts/pairwise-correlation/dataExtractors';
import { Dates, type ExcelCell, plural, ptToPx } from 'venn-utils';
import type { TabularDataRow } from '../../../studio-blocks/components/charts/pairwise-correlation/types';
import useExportUpdate from '../../../studio-blocks/logic/useExportUpdate';
import { EllipsisTooltipSpan, Hint } from 'venn-ui-kit';
import CorrelationColorLegend from '../../../studio-blocks/components/charts/pairwise-correlation/CorrelationColorLegend';
import {
  CorrelationColumnHeaderCell,
  CorrelationLabelHeaderCell,
  CorrelationValueCell,
  DATA_COLUMN_MIN_WIDTH_PX,
  HEADER_HEIGHT_PX,
  LABEL_CELL_TOP_PADDING_PX,
  LABEL_COLUMN_WIDTH_PX,
  REPORT_LAB_DATA_COLUMN_MIN_WIDTH_PX,
  ROW_HEIGHT_FONT_MULTIPLIER,
} from '../../../studio-blocks/components/charts/pairwise-correlation/agGridColumns';
import { FIRST_COLUMN_INNER_WIDTH } from '../../components/visualizations/grids/correlation-grid';
import { usePageProxySymbol } from '../../components/core/block-subject-footnotes-and-superscripts';
import { toTypeUnsafeSubject } from '../../../studio-blocks/logic/exportUtils';
import { useIsCorrelationLabelFull } from '../../../studio-blocks/logic/useCorrelationLabelDisplay';
import { OptionIcon } from '../../../common/OptionIcon';
import { useBlockContext } from '../../contexts/block-context';
import { GridWrapper } from '../../../studio-blocks/common';
import { useCommonGridProps } from '../../../studio-blocks/logic/columnUtils';
import ExportableGrid from '../../../studio-blocks/components/grid/ExportableGrid';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';

type PortfolioCorrelationProps = Omit<AnalysisProps, 'analyses'> & {
  analysis: Analysis | undefined;
};

export const PortfolioCorrelation = ({
  id,
  requests,
  analysis,
  exportMetaData,
  inPrintMode,
  isReport,
}: PortfolioCorrelationProps) => {
  const proxySymbol = usePageProxySymbol();
  const tableFont = useRecoilValue(blockFonts.blockTableData(id));
  const legendFont = useRecoilValue(blockFonts.blockChartLegend(id));
  const proxyMap = useRecoilValue(flattenedProxyMapState);
  const printOrientation = useRecoilValue(studioPrintOrientationType);
  const header = useRecoilValue(blockDisplayHeader(id));
  const isFullLabelDisplay = useIsCorrelationLabelFull();
  const { setTrailerElement } = useBlockContext();
  const correlations =
    analysis?.analysisType === 'CORRELATION'
      ? compact(analysis?.correlationMatrix?.correlations)
      : compact(analysis?.downsideCorrelationMatrix?.correlations); // only display first portfolio
  const hasBenchmark = requests[0]!.benchmark !== undefined;
  const numberExcluded = correlations.length - MAX_INVESTMENTS;

  const subject = useRecoilValue(blockLimitedRequestSubjects(id))[0];
  const asOfDate = useRecoilValue(subjectAsOfDate([id, subject ? toTypeUnsafeSubject(subject) : undefined]));

  const data = useMemo(
    () => portfolioCorrelationExtractor(correlations, requests[0]!.subject.portfolio, hasBenchmark),
    [correlations, requests, hasBenchmark],
  );

  const dataRef = useRef(data);

  if (!isEqual(dataRef.current, data)) {
    dataRef.current = data;
  }

  const excelDataFn = useCallback(() => {
    const isRelative = requests[0]?.relative;
    const benchmarkName = requests[0]!.benchmark?.name ?? '--';
    const headerRow: ExcelCell[] = dataRef.current.map((data: TabularDataRow) => ({
      value: isRelative ? `${data.label} (Relative to ${benchmarkName})` : data.label,
      bold: true,
    }));
    const body: ExcelCell[][] = dataRef.current.map((row: TabularDataRow) => {
      const cellData: ExcelCell[] = [];
      for (let subjectIdx = 0; subjectIdx < correlations.length; subjectIdx++) {
        cellData.push({
          value: row[subjectIdx]?.value ?? '--',
          percentage: row?.type === 'percentage',
          digits: correlations.length,
        });
      }
      return [{ value: row.label, bold: true }, ...cellData];
    });
    return compact([
      subject && asOfDate && [{ value: `${subject.name} (As of ${Dates.toDayMonthYear(asOfDate)})`, bold: true }],
      [{ value: header, bold: true }, ...headerRow],
      ...body,
    ]);
  }, [requests, header, correlations.length, subject, asOfDate]);

  useExportUpdate({ selectedRefId: id, exportMetaData, excelDataFn });

  const correlationLabelCellRenderer = useCallback(
    (params: ICellRendererParams<TabularDataRow>) => {
      const rowData = params.data;
      if (!rowData) return null;
      const labelContent = [rowData.fundId && proxyMap?.[rowData.fundId] && <sup>{proxySymbol}</sup>, rowData.label];
      const icon = rowData.optionData ? <OptionIcon optionType={rowData.optionData.optionType} /> : null;

      return isFullLabelDisplay ? (
        <span>
          {icon} {labelContent}
        </span>
      ) : (
        <EllipsisTooltipSpan usePortal maxWidth={FIRST_COLUMN_INNER_WIDTH}>
          {icon} {labelContent}
        </EllipsisTooltipSpan>
      );
    },
    [proxyMap, proxySymbol, isFullLabelDisplay],
  );

  const rowHeightPx = ptToPx(tableFont.fontSizePt) * ROW_HEIGHT_FONT_MULTIPLIER[printOrientation];
  const labelCellTopPaddingPx = LABEL_CELL_TOP_PADDING_PX[printOrientation];
  const dataColumnMinWidthPx = isReport ? REPORT_LAB_DATA_COLUMN_MIN_WIDTH_PX : DATA_COLUMN_MIN_WIDTH_PX;
  const cellFontSize = getCustomFontSize(tableFont);

  const extraDefaultColDef = useMemo<ColDef>(
    () => ({
      autoHeaderHeight: false,
      wrapHeaderText: false,
      cellStyle: { fontSize: cellFontSize },
    }),
    [cellFontSize],
  );
  const commonGridProps = useCommonGridProps(extraDefaultColDef);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        colId: 'label',
        width: LABEL_COLUMN_WIDTH_PX,
        suppressSizeToFit: true,
        sortable: false,
        resizable: false,
        headerComponent: CorrelationLabelHeaderCell,
        headerComponentParams: { fontSize: cellFontSize },
        cellStyle: { paddingLeft: 5, paddingTop: labelCellTopPaddingPx, fontSize: cellFontSize },
        cellRenderer: correlationLabelCellRenderer,
      },
      ...data.map(
        (col, colNum): ColDef => ({
          colId: `col-${colNum}`,
          minWidth: dataColumnMinWidthPx,
          sortable: false,
          resizable: false,
          headerComponent: CorrelationColumnHeaderCell,
          headerComponentParams: { columnLabel: col.label, optionData: col.optionData },
          cellStyle: { padding: 0 },
          cellRenderer: CorrelationValueCell,
          cellRendererParams: { colNum },
        }),
      ),
    ],
    [data, correlationLabelCellRenderer, cellFontSize, labelCellTopPaddingPx, dataColumnMinWidthPx],
  );

  return (
    <>
      <GridWrapper isReport={isReport} className="[&_.ag-header]:border-b-venn-black">
        <ExportableGrid
          {...commonGridProps}
          selectedRefId={id}
          exportable={false}
          inPrintMode={inPrintMode}
          isCompact={false}
          rowData={data}
          columnDefs={columnDefs}
          rowHeight={rowHeightPx}
          headerHeight={HEADER_HEIGHT_PX}
          domLayout={'autoHeight' as const}
          suppressRowVirtualisation
          suppressRowTransform
          scrollbarWidth={10}
        />
      </GridWrapper>
      <div ref={setTrailerElement} className="flow-root">
        <CorrelationColorLegend fontSize={getCustomFontSize(legendFont)} />
        {numberExcluded > 0 ? (
          <div className="flex flex-column justify-center px-[9px] pt-[9px]">
            <Hint>
              {plural(numberExcluded, {
                1: '{{count}} smaller investment has been excluded.',
                other: '{{count}} smaller investments have been excluded.',
              })}
            </Hint>
          </div>
        ) : null}
      </div>
    </>
  );
};
